// -----------------------------
// WebMCP (navigator.modelContext) typings
// -----------------------------
// WebMCP is still an evolving browser proposal (Chrome: chrome://flags/#enable-webmcp-testing),
// so these types are kept local rather than augmenting the global Navigator interface.
// Only the surface this app uses is described here. modelContext only exists in secure
// contexts (https, or http://localhost).

export interface WebMcpTextContent {
  type: 'text';
  text: string;
}

export interface WebMcpToolResult {
  content: WebMcpTextContent[];
  isError?: boolean;
}

/** Second argument passed by the browser to a tool's execute callback. */
export interface WebMcpClient {
  /** Pauses the agent while the page asks the human for input (e.g. a confirm dialog). */
  requestUserInteraction?: <T>(callback: () => Promise<T>) => Promise<T>;
}

export interface WebMcpTool<TInput = any> {
  name: string;
  title?: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: { readOnlyHint?: boolean };
  execute: (input: TInput, client?: WebMcpClient) => Promise<WebMcpToolResult>;
}

/** Some preview builds and polyfills return a handle from registerTool. */
export interface WebMcpRegistration {
  unregister?: () => void;
}

export interface ModelContextRegisterToolOptions {
  /** Aborting the signal unregisters the tool (current spec). */
  signal?: AbortSignal;
}

export interface ModelContext {
  // Current spec: returns a Promise and rejects if the name is already registered.
  // Earlier previews and polyfills returned a handle (or nothing) synchronously.
  registerTool(
    tool: WebMcpTool,
    options?: ModelContextRegisterToolOptions,
  ): Promise<void> | WebMcpRegistration | void;
  /** Only in earlier previews and polyfills; the current spec unregisters via AbortSignal. */
  unregisterTool?(name: string): void;
}

type WithModelContext = { modelContext?: ModelContext };

/**
 * Returns the browser's WebMCP entry point, or null when the browser does not support it.
 * Chrome 152+ exposes it as document.modelContext; earlier Chrome builds and polyfills
 * used navigator.modelContext (removed in Chrome 153), so both are checked, document first.
 */
export function getModelContext(): ModelContext | null {
  const candidates = [
    typeof document === 'undefined' ? undefined : (document as Document & WithModelContext).modelContext,
    typeof navigator === 'undefined' ? undefined : (navigator as Navigator & WithModelContext).modelContext,
  ];
  return candidates.find((candidate) => !!candidate && typeof candidate.registerTool === 'function') ?? null;
}
